import React, { useState, useEffect, useCallback } from "react";

import { PlainButton, BottomCenteredDrawer } from "@jugl-web/ui-kit";
import { SubscriptionIntervalType } from "@jugl-web/rest-api/entities/models/common-types/SubscriptionIntervalType";
import { SelectSubscriptionFormProvider } from "@web-src/modules/workspace/components/SelectSubscriptionForm/SelectSubscriptionFormProvider";
import { PlansStep, PLANS_STEP_CONTAINER_ID } from "./components/PlansStep";
import { ReactComponent as ArrowUp } from "./assets/arrow-up.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { SelectedPlanInfo } from "../CompletePurchaseDialog/components/SummaryStep/types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSelectPlan: (planInfo: SelectedPlanInfo) => void;
  onDowngrade?: (planInfo: SelectedPlanInfo) => void;
  currentPlanId?: string;
  currentInterval?: SubscriptionIntervalType;
};

export const WorkspaceSubscriptionDrawerContent: React.FC<Props> = ({
  isOpen,
  onClose,
  onSelectPlan,
  onDowngrade,
  currentPlanId,
  currentInterval,
}) => {
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  const getContainer = useCallback(
    () => document.getElementById(PLANS_STEP_CONTAINER_ID),
    []
  );

  useEffect(() => {
    const handleScroll = () => {
      const container = getContainer();
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const scrollPercent = (scrollTop / (scrollHeight - clientHeight)) * 100;
        setShowScrollToTop(scrollPercent > 50);
      }
    };

    const container = getContainer();
    container?.addEventListener("scroll", handleScroll);

    return () => {
      container?.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen, getContainer]);

  return (
    <BottomCenteredDrawer isOpen={isOpen} onClose={onClose} size="fullscreen">
      <PlainButton
        className="absolute top-6 right-6 z-20 h-8 w-8 rounded-lg bg-dark/20 backdrop-blur-[0.5px] transition-colors hover:bg-dark/30"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <CloseIcon className="cursor-pointer" />
      </PlainButton>
      {showScrollToTop && (
        <PlainButton
          className="absolute right-10 bottom-[20%] z-20 flex items-center justify-center rounded-full bg-black p-4"
          onClick={() => {
            getContainer()?.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <ArrowUp />
        </PlainButton>
      )}
      <PlansStep
        onSelectPlan={onSelectPlan}
        onDowngrade={onDowngrade}
        currentPlanId={currentPlanId}
        currentInterval={currentInterval}
      />
    </BottomCenteredDrawer>
  );
};

export const WorkspaceSubscriptionDrawer = (props: Props) => {
  return (
    <SelectSubscriptionFormProvider>
      <WorkspaceSubscriptionDrawerContent {...props} />
    </SelectSubscriptionFormProvider>
  );
};
